import {unref as vueUnref} from 'vue';
import NumberUtils from '@/core/utils/NumberUtils';
import DateSimple from "@/core/types/DateSimple";
import StringUtils from "@/core/utils/StringUtils";
import TimeStamp from "@/core/types/TimeStamp";
import Core from "@/core/Core";
import DateRange from "@/core/types/DateRange";

type DateLike = Date | string | number;

class DateUtils {
	/**
	 * Маппинг форматов на свойства Intl.DateTimeFormat
	 * @private
	 */
	static #FORMAT_MAP: Record<string, [keyof Intl.DateTimeFormatOptions, string]> = {
		// weekday
		d:    ['weekday', 'narrow'],
		dd:   ['weekday', 'short'],
		dddd: ['weekday', 'long'],

		// era
		e:    ['era', 'narrow'],
		ee:   ['era', 'short'],
		eeee: ['era', 'long'],

		// year
		yy:   ['year', '2-digit'],
		yyyy: ['year', 'numeric'],

		// month
		M:    ['month', 'numeric'],
		MM:   ['month', '2-digit'],
		Mo:   ['month', 'narrow'],
		MMM:  ['month', 'short'],
		MMMM: ['month', 'long'],

		// day
		D:  ['day', 'numeric'],
		DD: ['day', '2-digit'],

		// hour
		H:  ['hour', 'numeric'],
		HH: ['hour', '2-digit'],

		// minute
		m:  ['minute', 'numeric'],
		mm: ['minute', '2-digit'],

		// second
		s:  ['second', 'numeric'],
		ss: ['second', '2-digit'],

		// timeZone
		Z:  ['timeZoneName', 'short'],
		ZZ: ['timeZoneName', 'long'],
	};

	static ONE_DAY = 86400000; // 24 * 60 * 60 * 1000;
	static ONE_HOUR  = 3600000; // 60 * 60 * 1000;
	static ONE_MINUTE  = 60000; // 60 * 1000;

	/**
	 * Проверяет, является ли значение объектом Date
	 */
	static isDate(value: unknown): value is Date {
		value = vueUnref(value);
		return value instanceof Date;
	}

	/**
	 * Проверяет, можно ли значение преобразовать в валидную дату
	 */
	static isDateLike(value: unknown): value is DateLike {
		value = vueUnref(value);

		if (value === null || value === undefined) {
			return false;
		}

		if (value instanceof Date) {
			return !isNaN(value.getTime());
		}

		if (typeof value === 'string' || typeof value === 'number') {
			const date = new Date(value);
			return !isNaN(date.getTime());
		}

		return false;
	}

	/**
	 * Преобразует значение в объект Date, сохраняя тип если это уже Date-подобный объект
	 */
	static toDate(value: unknown): Date | null {
		value = vueUnref(value);

		if (!this.isDateLike(value)) {
			return null;
		}

		if (value instanceof Date) {
			return value;
		}

		return new Date(value);
	}

	/**
	 * Представляет дату как ISO строку в локальном времени (в отличие от Date.toISOString() который возвращает UTC)
	 * @param date - Дата для форматирования
	 * @param includeTimezone - Включать ли информацию о таймзоне (по умолчанию true)
	 */
	static asISOString(date: unknown, includeTimezone: boolean = true): string | null {
		date = vueUnref(date);

		if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
			return null;
		}

		const pad = NumberUtils.leftPadZero;

		const year         = date.getFullYear();
		const month        = pad(date.getMonth() + 1);
		const day          = pad(date.getDate());
		const hours        = pad(date.getHours());
		const minutes      = pad(date.getMinutes());
		const seconds      = pad(date.getSeconds());
		const milliseconds = pad(date.getMilliseconds(), 3);

		let result = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;

		/** код ниже добавляет таймзону */
		if (includeTimezone) {
			result += Core.timeZone.UTCOffset;
		}

		return result;
	}

	/**
	 * Сравнивает две даты
	 * @returns -1, 0, или 1, или null если одна из дат некорректная
	 */
	static compareDates(date1: unknown, date2: unknown): number | null {
		date1 = vueUnref(date1);
		date2 = vueUnref(date2);

		if (!date1 || !date2 || !(date1 instanceof Date) || !(date2 instanceof Date) ||
			isNaN(date1.getTime()) || isNaN(date2.getTime())) {
			return null;
		}

		const time1 = date1.getTime();
		const time2 = date2.getTime();

		if (time1 < time2) return -1;
		if (time1 > time2) return 1;
		return 0;
	}

	/**
	 * Проверяет, является ли дата сегодняшней
	 */
	static isToday(date: unknown): boolean {
		const dateObject = DateUtils.toDate(date);
		if (!dateObject) {
			return false;
		}

		const today = new Date();
		return (
			dateObject.getDate() === today.getDate()
			&& dateObject.getMonth() === today.getMonth()
			&& dateObject.getFullYear() === today.getFullYear()
		);
	}

	/**
	 * Вычисляет количество дней между двумя датами
	 * @param date1 - Первая дата (Date или timestamp)
	 * @param date2 - Вторая дата (Date или timestamp)
	 */
	static getDaysBetweenDates(date1: Date | number, date2: Date | number): number {
		if (!(date1 instanceof Date) && typeof date1 !== 'number') {
			throw new Error('Первый аргумент должен быть объектом Date или временной меткой.');
		}
		if (!(date2 instanceof Date) && typeof date2 !== 'number') {
			throw new Error('Второй аргумент должен быть объектом Date или временной меткой.');
		}

		const d1 = DateUtils.toDayStart(new Date(date1))!;
		const d2 = DateUtils.toDayStart(new Date(date2))!;

		const differenceInTime = Math.abs(d2.getTime() - d1.getTime());
		return Math.ceil(differenceInTime / (1000 * 3600 * 24));
	}

	/**
	 * Возвращает название дня недели для переданной даты
	 * @param date - Дата в любом валидном формате
	 * @param fullDayName - Возвращать полное название (Понедельник) или сокращенное (Пн)
	 * @param lowerCase - Возвращать в нижнем регистре
	 */
	static getWeekDayName(date: unknown, fullDayName: boolean = true, lowerCase: boolean = false): string | null {
		const dateObject = DateUtils.toDate(date);
		if (!dateObject) {
			return null;
		}

		const shortNames = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
		const fullNames  = [
			'Воскресенье',
			'Понедельник',
			'Вторник',
			'Среда',
			'Четверг',
			'Пятница',
			'Суббота'
		];

		const dayIndex = dateObject.getDay();
		const dayName  = fullDayName ? fullNames[dayIndex] : shortNames[dayIndex];

		return lowerCase ? dayName.toLowerCase() : dayName;
	}

	/**
	 * Проверяет, является ли дата выходным днем (суббота или воскресенье)
	 */
	static isWeekend(date: unknown): boolean | null {
		const dateObject = DateUtils.toDate(date);
		if (!dateObject) {
			return null;
		}

		return dateObject.getDay() === 0 || dateObject.getDay() === 6;
	}

	/**
	 * Форматирует дату как "день месяц" с опциональным временем
	 * @param date - Дата для форматирования
	 * @param timeFormat - Формат времени или null чтобы не показывать время
	 * @param lowerCase - Возвращать в нижнем регистре
	 */
	static formatDateWithMonth(date: unknown, timeFormat: string | null = 'HH:mm', lowerCase: boolean = false): string | null {
		const dateObject = DateUtils.toDate(date);
		if (!dateObject) {
			return null;
		}

		const today = new Date();
		let result  = '';

		// Определяем нужен ли год
		if (dateObject.getFullYear() !== today.getFullYear()) {
			// Формат: "25 декабря 2022"
			result = new Intl.DateTimeFormat('ru-RU', {
				day:   'numeric',
				month: 'long',
				year:  'numeric'
			}).format(dateObject);
		} else {
			// Формат: "25 декабря"
			result = new Intl.DateTimeFormat('ru-RU', {
				day:   'numeric',
				month: 'long'
			}).format(dateObject);
		}

		// Добавляем время если указан формат
		if (timeFormat) {
			const timeStr = new Intl.DateTimeFormat('ru-RU', {
				hour:   '2-digit',
				minute: '2-digit',
				hour12: false
			}).format(dateObject);
			result += ` в ${timeStr}`;
		}

		return lowerCase ? result.toLowerCase() : result;
	}

	/**
	 * Форматирует дату согласно заданному формату и локали
	 *
	 * @param date - объект даты или строка для Date.parse
	 * @param format - формат через разделитель | (токены: d, dd, dddd, yy, yyyy, M, MM, Mo, MMM, MMMM, D, DD, H, HH, m, mm, s, ss, Z, ZZ)
	 * @param locale - локаль для форматирования (по умолчанию 'ru-RU')
	 * @returns Отформатированная строка или null если дата невалидна
	 *
	 * @example
	 * // Полная дата
	 * DateUtils.format(new Date(), 'dddd|DD|MMMM|yyyy'); // "среда, 26 марта 2025"
	 *
	 * @example
	 * // Короткая дата
	 * DateUtils.format(new Date(), 'dd|DD|MMM|yy'); // "ср, 26 мар 25"
	 *
	 * @example
	 * // Числовой формат
	 * DateUtils.format(new Date(), 'DD|MM|yyyy'); // "26.03.2025"
	 *
	 * @example
	 * // Дата и время
	 * DateUtils.format(new Date(), 'DD|MM|yyyy|HH|mm|ss'); // "26.03.2025 20:51:19"
	 *
	 * @example
	 * // Только время
	 * DateUtils.format(new Date(), 'HH|mm'); // "20:51"
	 *
	 * @example
	 * // 12-часовой формат
	 * DateUtils.format(new Date(), '12|H|mm'); // "8:51 PM"
	 *
	 * @example
	 * // 24-часовой формат
	 * DateUtils.format(new Date(), '24|HH|mm'); // "20:51"
	 *
	 * @example
	 * // С днём недели
	 * DateUtils.format(new Date(), 'd|DD|M|yyyy'); // "С 26 3 2025"
	 *
	 * @example
	 * // С таймзоной
	 * DateUtils.format(new Date(), 'DD|MM|yyyy|HH|mm|Z'); // "26.03.2025 20:51 GMT+7"
	 *
	 * @example
	 * // Полная таймзона
	 * DateUtils.format(new Date(), 'DD|MM|yyyy|HH|mm|ZZ'); // "26.03.2025 20:51 Красноярск, стандартное время"
	 *
	 * @example
	 * // Месяц узким стилем
	 * DateUtils.format(new Date(), 'DD|Mo|yyyy'); // "26 М 2025"
	 *
	 * @example
	 * // С эрой
	 * DateUtils.format(new Date(), 'DD|MM|yyyy|eeee'); // "26.03.2025 от Рождества Христова"
	 *
	 * @example
	 * // Английская локаль
	 * DateUtils.format(new Date(), 'dddd|MMMM|DD|yyyy', 'en-US'); // "Wednesday, March 26, 2025"
	 *
	 * @example
	 * // Без формата (дефолт)
	 * DateUtils.format(new Date()); // "26.03.2025"
	 *
	 * @example
	 * // Из строки
	 * DateUtils.format('2025-03-26', 'DD|MM|yyyy'); // "26.03.2025"
	 *
	 * @example
	 * // Невалидная дата
	 * DateUtils.format('invalid', 'DD|MM|yyyy'); // null
	 *
	 * @example
	 * // Однозначные числа (без ведущего нуля)
	 * DateUtils.format(new Date('2025-03-05'), 'D|M|yyyy'); // "5 3 2025"
	 *
	 * @example
	 * // Двузначные числа (с ведущим нулём)
	 * DateUtils.format(new Date('2025-03-05'), 'DD|MM|yyyy'); // "05.03.2025"
	 */
	static format(date: unknown, format: string = '', locale: string = 'ru-RU'): string | null {
		date = vueUnref(date);

		if (!DateUtils.isDateLike(date)) return null;

		const dateObj = DateUtils.toDate(date);
		if (!dateObj) return null;

		// Если формат не задан, используем дефолтный
		if (!format) return new Intl.DateTimeFormat(locale).format(dateObj);

		// Парсим формат
		const options: Intl.DateTimeFormatOptions = {};
		const formatTokens = format.split('|').filter(Boolean);

		for (const token of formatTokens) {
			if (token === '12' || token === '24') {
				options.hour12 = token === '12';
			} else if (this.#FORMAT_MAP[token]) {
				const [prop, value] = this.#FORMAT_MAP[token];
				options[prop] = value as any;
			}
		}

		return new Intl.DateTimeFormat(locale, options).format(dateObj);
	}

	/**
	 * Форматирует период в строку с учетом месяца и года начала периода и конца:
	 * - если период находится в пределах текущего года - год не выводится
	 * - если период находится в пределах одного месяца - название месяца выводится один раз,
	 *   даты начала и окончания разделяются дефисом
	 * - если период находится в пределах одного дня - выводится только этот день, без дефиса
	 * @param startDate - начальная дата
	 * @param endDate - конечная дата
	 */
	static formatRange(startDate: unknown, endDate: unknown): string {
		startDate = vueUnref(startDate);
		endDate = vueUnref(endDate);

		if (!startDate && !endDate) {
			return "Дата не выбрана";
		}

		const start = startDate ? this.toDate(startDate) : null;
		const end = endDate ? this.toDate(endDate) : null;

		const startDateString = start ? this.format(start) : null;
		const endDateString = end ? this.format(end) : null;
		const today = new Date();
		const currentYear = today.getFullYear();
		let result = "";

		if (
			startDateString === endDateString ||
			(startDateString && !endDateString)
		) {
			if (start) {
				result = `${start.getDate()} ${this.getMonthName(
					start.getMonth(),
					"short"
				)}`;
				if (start.getFullYear() !== currentYear) {
					result += ` ${start.getFullYear()}`;
				}

				if (!end) {
					result += "...";
				}
			}
			return result;
		}

		if (end) {
			if (start) {
				if (start.getFullYear() === end.getFullYear()) {
					if (start.getMonth() === end.getMonth()) {
						result = `${start.getDate()} - ${end.getDate()} ${this.getMonthName(
							start.getMonth(),
							"short"
						)}`;
					} else {
						result = `${start.getDate()} ${this.getMonthName(
							start.getMonth(),
							"short"
						)} - ${end.getDate()} ${this.getMonthName(
							end.getMonth(),
							"short"
						)}`;
					}

					if (start.getFullYear() !== currentYear) {
						result += ` ${start.getFullYear()}`;
					}
				} else {
					result += `${startDateString} - ${endDateString}`;
				}
			} else {
				result += "...";
				result += `${end.getDate()} ${this.getMonthName(
					end.getMonth(),
					"short"
				)}`;
				if (end.getFullYear() !== currentYear) {
					result += ` ${end.getFullYear()}`;
				}
			}
		}
		return result;
	}

	/**
	 * Форматирует дату в человекочитаемом виде с опциональным временем
	 * @param value - дата для форматирования
	 * @param timeFormat - формат времени или null для отключения
	 * @param lower - привести к нижнему регистру
	 */
	static humanizeDate(value: unknown, timeFormat: string | null = 'HH|mm', lower: boolean = false): string | null {
		if (!DateUtils.isDateLike(value)) return null;

		const date = DateUtils.toDate(value);
		if (!date) return null;

		const today = new Date();

		let result = '';

		if (date.getFullYear() !== today.getFullYear()) {
			result = DateUtils.format(date, 'D|MMMM|yyyy')!;
		} else if (date.getMonth() !== today.getMonth()) {
			result = DateUtils.format(date, 'D|MMMM')!;
		} else {
			const dayDiff      = date.getDate() - today.getDate();
			const relativeDays: Record<number, string> = {
				2:    'Послезавтра',
				1:    'Завтра',
				0:    'Сегодня',
				[-1]: 'Вчера',
				[-2]: 'Позавчера'
			};

			result = relativeDays[dayDiff] || DateUtils.format(date, 'D|MMMM')!;
		}

		if (timeFormat) {
			result += ` в ${DateUtils.format(date, timeFormat)}`;
		}

		if (lower) {
			result = result.toLowerCase();
		}
		return result;
	}

	/**
	 * Форматирует диапазон дат в человекочитаемом виде
	 * @param start - начальная дата
	 * @param end - конечная дата
	 * @param separator - разделитель между датами
	 */
	static formatDateRange(start: unknown, end: unknown, separator: string = ' по '): string {
		if (!this.isDateLike(start) || !this.isDateLike(end)) return '';

		const startDate = this.toDate(start)!;
		const endDate   = this.toDate(end)!;

		// Определяем форматы в зависимости от года
		const isSameYear  = startDate.getFullYear() === endDate.getFullYear();
		const startFormat = isSameYear ? 'D|MMMM' : 'D|MMMM|yyyy';
		const endFormat   = 'D|MMMM|yyyy';

		return `${this.format(startDate, startFormat)}${separator}${this.format(endDate, endFormat)}`;
	}


	/**
	 * Клонирует объект даты
	 */
	static cloneDate(date: unknown): Date | null {
		date = vueUnref(date);
		if (!this.isDateLike(date)) return null;
		const dateObj = this.toDate(date);
		return dateObj ? new Date(dateObj.valueOf()) : null;
	}

	/**
	 * Возвращает дату в начале дня (00:00:00.000)
	 */
	static toDayStart(date: unknown): Date | null {
		if (!this.isDateLike(date)) return null;
		const result = DateUtils.cloneDate(date);
		if (!result) return null;
		result.setHours(0, 0, 0, 0);
		return result;
	}

	/**
	 * Возвращает дату в конце дня (23:59:59.999)
	 */
	static toDayEnd(date: unknown): Date | null {
		if (!this.isDateLike(date)) return null;
		const result = DateUtils.cloneDate(date);
		if (!result) return null;
		result.setHours(23, 59, 59, 999);
		return result;
	}

	/**
	 * Вычисляет разницу между двумя датами
	 * @param from - начальная дата
	 * @param to - конечная дата
	 * @param format - единицы измерения ('days', 'hours', 'minutes', 'seconds')
	 */
	static dateDifference(from: Date, to: Date, format: string = "days"): number | null {
		if (!DateUtils.isDate(from) || !DateUtils.isDate(to)) return null;

		const utc1 = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
		const utc2 = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());

		let formatTime = 1;
		switch (format) {
			case "days":
				formatTime = 1000 * 60 * 60 * 24;
				break;
			case "hours":
				formatTime = 1000 * 60 * 60;
				break;
			case "minutes":
				formatTime = 1000 * 60;
				break;
			case "seconds":
				formatTime = 1000;
				break;
		}

		return Math.floor((utc2 - utc1) / formatTime);
	}

	/**
	 * Вычисляет разницу между двумя датами в миллисекундах
	 *
	 * Метод автоматически приводит обе даты к UTC (через getTime()),
	 * поэтому таймзоны не влияют на результат - сравнивается реальное время.
	 * Разница всегда возвращается как положительное число.
	 *
	 * @param date1 - Первая дата для сравнения
	 * @param date2 - Вторая дата для сравнения
	 * @returns Абсолютная разница в миллисекундах между датами
	 *
	 * @example
	 * // Разные таймзоны, но разница корректно вычисляется
	 * const moscowDate = new Date('2024-11-07T15:00:00+03:00');
	 * const berlinDate = new Date('2024-11-07T15:30:00+02:00');
	 * const diff = getDateDifference(moscowDate, berlinDate);
	 * console.log(diff); // 5400000 (90 минут * 60 секунд * 1000 мс)
	 *
	 * @example
	 * // Одно и то же реальное время в разных таймзонах
	 * const date1 = new Date('2024-11-07T15:00:00+03:00'); // 12:00 UTC
	 * const date2 = new Date('2024-11-07T13:00:00+01:00'); // 12:00 UTC
	 * const diff = getDateDifference(date1, date2);
	 * console.log(diff); // 0 (одно и то же время)
	 *
	 * @example
	 * // Конвертация в другие единицы
	 * const start = new Date('2024-11-07T10:00:00');
	 * const end = new Date('2024-11-07T12:30:00');
	 * const diffMs = getDateDifference(start, end);
	 * const diffMinutes = diffMs / (1000 * 60); // 150 минут
	 * const diffHours = diffMs / (1000 * 60 * 60); // 2.5 часа
	 */
	static getDateTimeDifference(date1: TimeStamp|Date, date2: TimeStamp|Date): number {
		// Приводим обе даты к UTC (миллисекунды с Unix epoch)
		const time1 = date1.getTime();
		const time2 = date2.getTime();

		// Разница в миллисекундах (всегда положительная)
		return Math.abs(time2 - time1);
	}

	static formatDuration(duration: number) {
		const days    = Math.floor(duration / this.ONE_DAY);
		const hours   = Math.floor((duration % this.ONE_DAY) / this.ONE_HOUR);
		const minutes = Math.floor((duration % this.ONE_HOUR) / this.ONE_MINUTE);
		const seconds = Math.floor((duration % this.ONE_MINUTE) / 1000);

		return {
			days:    StringUtils.leftPad(days),
			hours:   StringUtils.leftPad(hours),
			minutes: StringUtils.leftPad(minutes),
			seconds: StringUtils.leftPad(seconds),
		};
	}

	/**
	 * Проверяет, является ли строка валидной датой в формате DD.MM.YYYY
	 * @param dateString - Строка даты для проверки
	 */
	static validInputString(dateString: unknown): dateString is string {
		dateString = vueUnref(dateString);

		// Проверка базовых условий
		if (!dateString || typeof dateString !== "string") {
			return false;
		}

		const delimiter = '.';
		const expectedLength = 10; // DD.MM.YYYY или MM.DD.YYYY

		// Проверка длины
		if (dateString.length !== expectedLength) {
			return false;
		}

		// Проверка позиций разделителей
		const parts = dateString.split(delimiter);
		if (parts.length !== 3) {
			return false;
		}

		// Проверка формата: 2 цифры, разделитель, 2 цифры, разделитель, 4 цифры
		const [day, month, year] = parts;
		return day.length === 2 &&
			month.length === 2 &&
			year.length === 4 &&
			/^\d+$/.test(day) &&
			/^\d+$/.test(month) &&
			/^\d+$/.test(year);
	}

	/**
	 * Создает объект даты из строки даты созданной по маске
	 * @param dateString - Строка даты для парсинга
	 * @param userOrder - Массив ["day", "month", "year"] в нужном порядке
	 */
	static parseInputString(dateString: unknown, userOrder: Array<'day' | 'month' | 'year'> | null = null): Date | null {
		dateString = vueUnref(dateString);

		if (typeof dateString !== 'string') {
			return null;
		}

		const validString = /^[\d]{1,4}(\.|-|\\|\/|\s)[\d]{1,4}(\.|-|\\|\/|\s)[\d]{1,4}$/gi.exec(dateString);

		if (!validString) {
			return null;
		}

		if (Array.isArray(userOrder)) {
			const validOrder = userOrder.length === 3 &&
				userOrder.every((item) => ['day', 'month', 'year'].includes(item));
			if (!validOrder) {
				throw new Error('userOrder must be array ["day", "month", "year"] with any order');
			}
		}

		const order: Array<'day' | 'month' | 'year'> = Array.isArray(userOrder) ? userOrder : ['day', 'month', 'year'];
		const date = {
			year: null as number | null,
			month: null as number | null,
			day: null as number | null,
		};

		dateString.split(/\.|-|\\|\/|\s/gi).forEach((part, index) => {
			const propName = order[index];
			switch (propName) {
				case "day": {
					const parsedPart = parseInt(part, 10);
					date[propName] = !isNaN(parsedPart) && parsedPart > 0 && parsedPart < 32
						? parsedPart
						: null;
					break;
				}
				case "month": {
					const parsedPart = parseInt(part, 10);
					date[propName] = !isNaN(parsedPart) && parsedPart > 0 && parsedPart < 13
						? parsedPart - 1
						: null;
					break;
				}
				case "year": {
					date[propName] = null;
					if (typeof part === "string") {
						date[propName] = part.length === 2
							? 2000 + parseInt(part, 10)
							: parseInt(part, 10);
					}
					break;
				}
			}
		});

		return Object.values(date).some((item) => item === null)
			? null
			: new Date(date.year!, date.month!, date.day!);
	}

	/**
	 * Валидирует и создает объект даты из строки даты, созданной по маске
	 * @param dateString - Строка даты
	 */
	static createFromInputString(dateString: unknown): Date | null {
		if (!DateUtils.validInputString(dateString)) {
			return null;
		}
		return DateUtils.parseInputString(dateString);
	}

	/**
	 * Возвращает массив дней месяца для отрисовки календаря
	 * Массив включает null-значения в начале и конце для заполнения неполных недель
	 * @param month - Номер месяца (0-11, где 0 = январь)
	 * @param year - Год (например, 2024)
	 */
	static getMonthDays(month: number, year: number): Array<number | null> {
		const firstDayOfWeek = 1;
		const monthDays = this.getMonthLength(month, year);
		const result: Array<number | null> = [];

		// Добавляем пустые ячейки в начале для дней предыдущего месяца
		const firstDayOfMonth = new Date(year, month, 1).getDay();
		const emptyDaysAtStart = this.calculateEmptyDays(firstDayOfMonth, firstDayOfWeek);

		for (let i = 0; i < emptyDaysAtStart; i++) {
			result.push(null);
		}

		// Добавляем дни текущего месяца
		for (let day = 1; day <= monthDays; day++) {
			result.push(day);
		}

		// Добавляем пустые ячейки в конце для дней следующего месяца
		const lastDayOfMonth = new Date(year, month, monthDays).getDay();
		const emptyDaysAtEnd = this.calculateEmptyDays(firstDayOfWeek, lastDayOfMonth + 1);

		for (let i = 0; i < emptyDaysAtEnd; i++) {
			result.push(null);
		}

		return result;
	}

	/**
	 * Возвращает объект даты первого дня текущей недели
	 * @returns {Date}
	 */
	static getCurrentWeekStartDate() {
		const today = new Date();
		const firstDayOfWeek = 1;
		if (today.getDay() === firstDayOfWeek) {
			return today;
		}
		if (today.getDay() > firstDayOfWeek) {
			const day = today.getDate() - (today.getDay() - firstDayOfWeek);
			today.setDate(day);
			return today;
		}

		const day = 7 - (firstDayOfWeek - today.getDay());
		today.setDate(today.getDate() - day);
		return today;
	}

	/**
	 * Вычисляет количество пустых ячеек между двумя днями недели
	 * @private
	 * @param targetDay - Целевой день недели (0-6)
	 * @param currentDay - Текущий день недели (0-6)
	 */
	static calculateEmptyDays(targetDay: number, currentDay: number): number {
		let diff = targetDay - currentDay;
		if (diff < 0) {
			diff += 7;
		}
		return diff;
	}

	/**
	 * Возвращает локализованное название месяца
	 * @param month - Номер месяца (0-11, где 0 = январь)
	 * @param format - Формат отображения
	 */
	static getMonthName(month: number, format: 'long' | 'short' | 'narrow' = "long"): string {
		const formatter = new Intl.DateTimeFormat('ru-RU', { month: format });
		// Используем фиксированный год для стабильности
		return formatter.format(new Date(2000, month, 1));
	}

	/**
	 * Сдвигает элементы массива на указанное количество позиций
	 * @param arr - Исходный массив
	 * @param count - Количество позиций для сдвига
	 */
	static rotateArray(arr: string[], count: number): string[] {
		return arr.slice(count).concat(arr.slice(0, count));
	}

	/**
	 * Возвращает массив локализованных названий дней недели
	 * @param firstDayOfWeek - Первый день недели (0 = воскресенье, 1 = понедельник, и т.д.)
	 */
	static getWeekDays(firstDayOfWeek: number): string[] {
		const formatter = new Intl.DateTimeFormat('ru-RU', { weekday: "short" });
		const weekdays: string[] = [];

		// Создаем массив дней недели, начиная с воскресенья
		// Используем фиксированную дату, которая начинается с воскресенья
		const baseDate = new Date(2023, 0, 1); // 1 января 2023 - воскресенье

		for (let i = 0; i < 7; i++) {
			const date = new Date(baseDate);
			date.setDate(baseDate.getDate() + i);
			weekdays.push(formatter.format(date));
		}

		// Если первый день недели не воскресенье, сдвигаем массив
		if (firstDayOfWeek !== 0) {
			return this.rotateArray(weekdays, firstDayOfWeek);
		}

		return weekdays;
	}

	/**
	 * Возвращает количество дней в указанном месяце,
	 * учитывает високосные годы для февраля
	 * @param month - Номер месяца (0-11, где 0 = январь)
	 * @param year - Год (например, 2024)
	 */
	static getMonthLength(month: number, year: number): number {
		return new Date(year, month + 1, 0).getDate();
	}

	/**
	 * Возвращает маску для ввода даты в формате DD.MM.YYYY
	 */
	static get dateMask(): string {
		return ["##", "##", "####"].join('.');
	}

	/**
	 * Проверяет, относятся ли две даты к одному и тому же году
	 * @param {Date | string | number} date1 - Первая дата для сравнения
	 * @param {Date | string | number} date2 - Вторая дата для сравнения
	 * @returns {boolean} - true если обе даты в одном году, false если в разных
	 *
	 * @example
	 * isSameYear(new Date('2025-01-01'), new Date('2025-12-31')) // true
	 *
	 * @example
	 * isSameYear('2024-12-31', '2025-01-01') // false
	 *
	 * @example
	 * isSameYear(Date.now(), new Date('2025-06-15')) // true (если текущий год 2025)
	 *
	 * @example
	 * isSameYear(1704067200000, 1735689599000) // true (оба timestamp в 2024 году)
	 */
	static isSameYear(date1: Date | string | number, date2: Date | string | number): boolean {
		const firstDate = new DateSimple(date1);
		const secondDate = new DateSimple(date2);

		return firstDate.getFullYear() === secondDate.getFullYear();
	}

	/**
	 * Получить таймзону из Date в формате "+HH" или "-HH"
	 * @param date - Дата из которой извлекается таймзона
	 * @returns Строка таймзоны в формате "+03" или "-05"
	 */
	static getTimezoneString(date: Date): string {
		// Получаем offset в минутах
		// getTimezoneOffset() возвращает инвертированный знак, поэтому инвертируем обратно
		const offsetMinutes = -date.getTimezoneOffset();

		// Знак
		const sign = offsetMinutes >= 0 ? '+' : '-';

		// Абсолютное значение
		const absMinutes = Math.abs(offsetMinutes);

		// Часы и минуты
		const hours = Math.floor(absMinutes / 60);

		// Форматирование с ведущими нулями
		const hoursStr = StringUtils.leftPad(hours);

		return `${sign}${hoursStr}`
	}

	/**
	 * Конвертирует миллисекунды в минуты
	 * @param {number} milliseconds - количество миллисекунд
	 * @returns {number} количество полных минут
	 * @example
	 * DateUtils.toMinutes(60000);  // 1
	 * DateUtils.toMinutes(90000);  // 1
	 * DateUtils.toMinutes(120000); // 2
	 */
	static toMinutes(milliseconds: number): number {
		return Math.floor(milliseconds / this.ONE_MINUTE);
	}

	/**
	 * Конвертирует миллисекунды в часы
	 * @param {number} milliseconds - количество миллисекунд
	 * @returns {number} количество полных часов
	 * @example
	 * DateUtils.toHours(3600000);  // 1
	 * DateUtils.toHours(5400000);  // 1
	 * DateUtils.toHours(7200000);  // 2
	 */
	static toHours(milliseconds: number): number {
		return Math.floor(milliseconds / this.ONE_HOUR);
	}

	/**
	 * Конвертирует миллисекунды в дни
	 * @param {number} milliseconds - количество миллисекунд
	 * @returns {number} количество полных дней
	 * @example
	 * DateUtils.toDays(86400000);  // 1
	 * DateUtils.toDays(90000000);  // 1
	 * DateUtils.toDays(172800000); // 2
	 */
	static toDays(milliseconds: number): number {
		return Math.floor(milliseconds / this.ONE_DAY);
	}

  /**
   * @deprecated Используйте напрямую shipmentPeriod.addDays(deliveryDays).
   *
   * Возвращает диапазон дат приёмки на основе диапазона отгрузки и количества
   * дней доставки.
   *
   * Метод оставлен для обратной совместимости с существующими местами вызова.
   * Делегирует в DateRange.addDays — вся реальная логика сдвига границ живёт там.
   *
   * @param shipmentPeriod — диапазон отгрузки
   * @param deliveryDays — количество дней доставки
   *
   * @example
   * const receiptPeriod = Utils.dateRange.getReceiptPeriodFromShipmentPeriod(shipmentPeriod, 3);
   * // эквивалент:
   * const receiptPeriod = shipmentPeriod.addDays(3);
   */
  static getReceiptPeriodFromShipmentPeriod(shipmentPeriod: DateRange, deliveryDays: number): DateRange {
    return shipmentPeriod.addDays(deliveryDays);
  }
}

export default DateUtils;
